import { useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Button,
  Avatar,
  Text,
  Badge,
  Divider,
  IconButton,
} from '@chakra-ui/react'
import { CloseIcon } from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import { usePollContext } from '../contexts/PollContext'

interface NavDrawerProps {
  user: { name?: string; picture?: string; title?: string } | null
  isOpen: boolean
  onClose: () => void
}

const navItems = [
  { label: '🏠 Dashboard', path: '/' },
  { label: '🍻 Meetings', path: '/meetings' },
  { label: '🍺 Beer Library', path: '/beers' },
  { label: '🙋 The Vote Tap', path: '/votes' },
  { label: '👥 Members', path: '/members' },
  { label: '📄 Documents', path: '/documents' },
  { label: '⚙️ Settings', path: '/settings' },
]

export default function NavDrawer({ user, isOpen, onClose }: NavDrawerProps) {
  const navigate = useNavigate()
  const { pendingCount: pendingPolls } = usePollContext()

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    localStorage.removeItem('user')
    window.location.reload()
  }

  return (
    <>
      {/* Backdrop — CSS opacity transition, GPU composited */}
      <Box
        position="fixed"
        inset={0}
        zIndex={10}
        bg="blackAlpha.600"
        style={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: isOpen ? 'opacity 0.4s' : 'opacity 0.25s',
        }}
        onClick={onClose}
      />

      {/* Panel — CSS transform transition, GPU composited, always in DOM */}
      <Box
        position="fixed"
        left={0}
        top={0}
        h="100vh"
        w="100%"
        maxW="28rem"
        zIndex={11}
        bg="gray.800"
        borderRight="1px solid"
        borderColor="gray.600"
        display="flex"
        flexDir="column"
        overflowY="auto"
        style={{
          height: '100dvh',
          transform: isOpen ? 'translateX(0)' : 'translateX(-110%)',
          transition: isOpen ? 'transform 0.4s ease' : 'transform 0.25s ease',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        <IconButton
          aria-label="Close menu"
          icon={<CloseIcon boxSize={3} />}
          onClick={onClose}
          position="absolute"
          top={2}
          right={2}
          variant="ghost"
          color="white"
          size="sm"
          _hover={{ bg: 'whiteAlpha.200' }}
        />

        <Box borderBottomWidth="1px" borderColor="gray.600" p={4} pr={10}>
          <HStack spacing={3}>
            <Avatar size="sm" src={user?.picture} name={user?.name} />
            <VStack align="start" spacing={0}>
              <Text fontSize="md" fontWeight="bold" color="white">{user?.name}</Text>
              <Text fontSize="xs" color="gray.500">{user?.title || 'Beer Enthusiast'}</Text>
            </VStack>
          </HStack>
        </Box>

        <VStack spacing={0} align="stretch" flex={1}>
          {navItems.map(({ label, path }) => (
            <Button
              key={label}
              variant="ghost"
              justifyContent="flex-start"
              p={4}
              h="auto"
              borderRadius="none"
              onClick={() => { navigate(path); onClose() }}
            >
              <HStack w="full" justify="space-between">
                <Text>{label}</Text>
                {path === '/votes' && pendingPolls > 0 && (
                  <Badge colorScheme="orange" borderRadius="full" fontSize="xs">{pendingPolls}</Badge>
                )}
              </HStack>
            </Button>
          ))}
          <Divider />
          <Button
            variant="ghost"
            justifyContent="flex-start"
            p={4}
            h="auto"
            borderRadius="none"
            color="red.500"
            onClick={handleLogout}
          >
            🚪 Logout
          </Button>
        </VStack>
      </Box>
    </>
  )
}
